import type {
  ApiPage,
  Buoy,
  CommandType,
  FirmwareStatus,
  DeviceCommand,
  ThemeMode,
  TrackPoint,
  FactoryJob,
  FactoryMode,
  FactorySimIdentity,
  FirmwareRelease,
} from "@buoy-x/domain";

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    message?: string,
  ) {
    super(message ?? code);
  }
}

export class BuoyXApi {
  constructor(
    public readonly baseUrl: string,
    private readonly getHeaders?: () => Promise<HeadersInit>,
  ) {}

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const headers = new Headers(init?.headers);
    if (!headers.has("content-type"))
      headers.set("content-type", "application/json");
    if (this.getHeaders) {
      const supplied = new Headers(await this.getHeaders());
      supplied.forEach((value, key) => headers.set(key, value));
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        code?: string;
        message?: string;
      };
      throw new ApiError(
        body.code ?? "REQUEST_FAILED",
        response.status,
        body.message,
      );
    }
    return response.status === 204
      ? (undefined as T)
      : (response.json() as Promise<T>);
  }

  me = () =>
    this.request<{
      id: string;
      name: string;
      email: string;
      role: "user" | "admin";
      themeMode: ThemeMode;
    }>("/api/v1/me");
  saveTheme = (themeMode: ThemeMode) =>
    this.request("/api/v1/me/preferences", {
      method: "PATCH",
      body: JSON.stringify({ themeMode }),
    });
  listBuoys = (cursor?: string) =>
    this.request<ApiPage<Buoy>>(
      `/api/v1/buoys${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    );
  getBuoy = (id: string) => this.request<Buoy>(`/api/v1/buoys/${id}`);
  firmwareStatus = (id: string) =>
    this.request<FirmwareStatus>(`/api/v1/buoys/${id}/firmware`);
  installFirmwareUpdate = (id: string) =>
    this.request<{ id: string; status: string; expiresAt: string }>(
      `/api/v1/buoys/${id}/firmware-update`,
      { method: "POST" },
    );
  claimBuoy = (serial: string, claimCode: string) =>
    this.request<Buoy>("/api/v1/buoy-claims", {
      method: "POST",
      body: JSON.stringify({ serial, claimCode }),
    });
  renameBuoy = (id: string, name: string) =>
    this.request<Buoy>(`/api/v1/buoys/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });
  removeBuoy = (id: string) =>
    this.request<void>(`/api/v1/buoys/${id}`, { method: "DELETE" });
  track = (id: string, from: string, to: string) =>
    this.request<{ points: TrackPoint[] }>(
      `/api/v1/buoys/${id}/track?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
  command = (
    id: string,
    type: CommandType,
    payload: Record<string, unknown> = {},
  ) => {
    const translated: Record<
      CommandType,
      { type: string; args?: Record<string, unknown> }
    > = {
      UP: { type: "MOVE_UP" },
      DOWN: { type: "MOVE_DOWN" },
      POWER_OFF: { type: "POWER_OFF" },
      LIGHT_OFF: { type: "LIGHT_SET", args: { mode: "off" } },
      LIGHT_ON: { type: "LIGHT_SET", args: { mode: "on" } },
      LIGHT_AUTO: { type: "LIGHT_SET", args: { mode: "auto" } },
      BUZZER_OFF: { type: "BUZZER_SET", args: { mode: "off" } },
      BUZZER_ON: { type: "BUZZER_SET", args: { mode: "on" } },
      BUZZER_AUTO: { type: "BUZZER_SET", args: { mode: "auto" } },
      STATUS_REFRESH: { type: "STATUS_GET" },
      GPS_LOCATION: { type: "GPS_FIX" },
    };
    const command = translated[type];
    return this.request(`/api/v1/buoys/${id}/commands`, {
      method: "POST",
      body: JSON.stringify({
        type: command.type,
        args: { ...command.args, ...payload },
      }),
    });
  };
  commands = (id: string) =>
    this.request<{ items: DeviceCommand[] }>(`/api/v1/buoys/${id}/commands`);
  adminUsers = (query = "") =>
    this.request<
      ApiPage<{
        id: string;
        name: string;
        email: string;
        role: string;
        createdAt: string;
      }>
    >(`/api/v1/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`);
  adminBuoys = (query = "") =>
    this.request<ApiPage<Buoy>>(
      `/api/v1/admin/buoys${query ? `?q=${encodeURIComponent(query)}` : ""}`,
    );
  adminAudit = (query = "") =>
    this.request<
      ApiPage<{
        id: number;
        actorUserId: string | null;
        action: string;
        targetType: string;
        targetId: string | null;
        createdAt: string;
      }>
    >(`/api/v1/admin/audit${query ? `?q=${encodeURIComponent(query)}` : ""}`);
  resetClaim = (id: string) =>
    this.request<{ claimCode: string; claimUrl: string }>(
      `/api/v1/admin/buoys/${id}/reset-claim`,
      { method: "POST", body: "{}" },
    );
  rotateMqtt = (id: string) =>
    this.request<{ username: string; password: string }>(
      `/api/v1/admin/buoys/${id}/rotate-mqtt-credential`,
      { method: "POST", body: "{}" },
    );

  factoryReleases = () =>
    this.request<{ enabled: boolean; releases: FirmwareRelease[] }>(
      "/api/v1/admin/factory/releases",
      { cache: "no-store" },
    );
  factoryJob = (id: string) =>
    this.request<FactoryJob>(
      `/api/v1/admin/factory/jobs/${encodeURIComponent(id)}`,
      { cache: "no-store" },
    );
  factoryNextSerial = () =>
    this.request<{ serial: string }>("/api/v1/admin/factory/next-serial", {
      cache: "no-store",
    });
  factoryDevice = (chipMac: string) =>
    this.request<{
      serial: string | null;
      mode: FactoryMode;
      initialize: boolean;
      operation: FactoryJob | null;
    }>(`/api/v1/admin/factory/devices/${encodeURIComponent(chipMac)}`, {
      cache: "no-store",
    });
  createFactoryJob = (input: {
    id: string;
    serial: string;
    chipMac: string;
    releaseId: string;
    mode: FactoryMode;
    eraseExisting: boolean;
    sim: FactorySimIdentity;
  }) =>
    this.request<FactoryJob>("/api/v1/admin/factory/jobs", {
      method: "POST",
      body: JSON.stringify(input),
      cache: "no-store",
    });
  factoryFlashed = (id: string) =>
    this.request<FactoryJob>(`/api/v1/admin/factory/jobs/${id}/flashed`, {
      method: "POST",
      body: "{}",
      cache: "no-store",
    });
  startFactoryFlash = (id: string) =>
    this.request<FactoryJob>(`/api/v1/admin/factory/jobs/${id}/start`, {
      method: "POST",
      body: "{}",
      cache: "no-store",
    });
  completeFactoryJob = (id: string, labelSaved: boolean) =>
    this.request<FactoryJob>(`/api/v1/admin/factory/jobs/${id}/complete`, {
      method: "POST",
      body: JSON.stringify({ labelSaved }),
      cache: "no-store",
    });

  async factoryBinary(id: string, name: string): Promise<Uint8Array> {
    const path =
      name === "label" ? "label" : `artifacts/${encodeURIComponent(name)}`;
    const headers = this.getHeaders ? await this.getHeaders() : undefined;
    const response = await fetch(
      `${this.baseUrl}/api/v1/admin/factory/jobs/${encodeURIComponent(id)}/${path}`,
      { credentials: "include", cache: "no-store", headers },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        code?: string;
      };
      throw new ApiError(body.code ?? "REQUEST_FAILED", response.status);
    }
    return new Uint8Array(await response.arrayBuffer());
  }
}
